import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import AdminNotificationService from '../services/AdminNotificationService';
import { useSession } from '../../contexts/SessionContext';

const slideIn = keyframes`
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(400px);
    opacity: 0;
  }
`;

const NotificationContainer = styled.div`
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  
  @media (max-width: 768px) {
    right: 10px;
    left: 10px;
    max-width: none;
  }
`;

const NotificationCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  border-left: 4px solid ${props => {
    switch(props.type) {
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      default: return '#3b82f6';
    }
  }};
  animation: ${props => props.closing ? slideOut : slideIn} 0.3s ease-out;
  position: relative;
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const NotificationTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NotificationMessage = styled.p`
  margin: 0;
  font-size: 14px;
  color: #4a5568;
  line-height: 1.5;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #a0aec0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    background: #f7fafc;
    color: #4a5568;
  }
`;

const getIcon = (type) => {
  switch(type) {
    case 'error': return <AlertCircle size={20} color="#ef4444" />;
    case 'warning': return <AlertTriangle size={20} color="#f59e0b" />;
    case 'success': return <CheckCircle size={20} color="#10b981" />;
    default: return <Info size={20} color="#3b82f6" />;
  }
};

export default function NotificationPopup() {
  const [notifications, setNotifications] = useState([]);
  const [closingIds, setClosingIds] = useState(new Set());
  const { user } = useSession();

  useEffect(() => {
    if (!user?.id) {
      return;
    }


    // Initial fetch
    const fetchNotifs = async () => {
      const notifs = await AdminNotificationService.fetchUnreadNotifications(user.id);
      const notificationTypeOnly = notifs.filter(n => n.display_type === 'notification' || !n.display_type);
      setNotifications(notificationTypeOnly);
    };

    fetchNotifs();

    // Subscribe to changes
    const unsubscribe = AdminNotificationService.subscribe((notifs) => {
      // Only show 'notification' type here (not 'popup')
      const notificationTypeOnly = notifs.filter(n => n.display_type === 'notification' || !n.display_type);
      setNotifications(notificationTypeOnly);
    });

    return () => {
      unsubscribe();
    };
  }, [user?.id]);

  const handleClose = async (notificationId) => {
    if (!user?.id) return;

    // Start closing animation
    setClosingIds(prev => new Set([...prev, notificationId]));

    // Wait for animation
    setTimeout(async () => {
      await AdminNotificationService.markAsViewed(user.id, notificationId);
      setClosingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }, 300);
  };

  if (notifications.length === 0) return null;

  return (
    <NotificationContainer>
      {notifications.map((notification) => (
        <NotificationCard 
          key={notification.id}
          type={notification.type}
          closing={closingIds.has(notification.id)}
        >
          <NotificationHeader>
            <NotificationTitle>
              {getIcon(notification.type)}
              {notification.title}
            </NotificationTitle>
            <CloseButton onClick={() => handleClose(notification.id)}>
              <X size={18} />
            </CloseButton>
          </NotificationHeader>
          <NotificationMessage>
            {notification.message}
          </NotificationMessage>
        </NotificationCard>
      ))}
    </NotificationContainer>
  );
}
